// native
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';

// service
import { ApiService } from './api.service';
import { GlobalsService } from './globals.service';
import { ItemsDisplayService } from './items-display.service';
import { CitationsQueueService } from './citations-queue.service';
import { UtilityService } from './utility.service';

// models
import { IDataItemQueryParams, IDisplayItem, IItem, CitationSelection, DocumentItemsResponse, IItemDataResponse, ISmartciteFetchedItem, DocumentCitations, ScrollSession, ItemsByOriginalId } from '../../models';

// constants
import { DEFAULT_LIBRARY_SORT_COLUMN, DEFAULT_SORT_ORDER, DEFAULT_QUERY_SIZE, BULK_REQUEST_SIZE } from '../../constants/constants';

@Injectable({
  providedIn: 'root'
})
export class ItemsSyncService {

  constructor(
    private apiService: ApiService,
    private globalsService: GlobalsService,
    private itemsDisplayService: ItemsDisplayService,
    private citationsQueueService: CitationsQueueService,
    private utilityService: UtilityService
  ) { }

  private getItemsSyncParams(params: IDataItemQueryParams, isScroll: boolean, scrollSession: ScrollSession): HttpParams {
    let httpParams = new HttpParams();
    if (params.query)
      httpParams = httpParams.append('query', params.query);

    if (params.created_since)
      httpParams = httpParams.set('created_since', params.created_since);

    if (params.recently_read)
      httpParams = httpParams.set('recently_read', true);

    if (params.star)
      httpParams = httpParams.set('star', 'true');

    if (params.tags)
      params.tags.forEach(tag => httpParams = httpParams.append('tags[]', tag));

    if (isScroll && scrollSession && !scrollSession.expired())
      httpParams = httpParams.set('scroll_id', scrollSession.id);

    httpParams = httpParams.append('sort[]', [this.itemsDisplayService.sort, this.itemsDisplayService.order].join(','));

    // extra sort filter added in case first sort filter results in multiple exact records with given sort criteria
    httpParams = httpParams.append('sort[]', [DEFAULT_LIBRARY_SORT_COLUMN, DEFAULT_SORT_ORDER].join(','));

    httpParams = httpParams.set('size', (params.size || DEFAULT_QUERY_SIZE).toString());

    return httpParams;
  }

  private mapItemData(items: IItem[]): IItem[] {
    return items.map((item: IItem) => {
      const extIds = item.ext_ids || {};
      const article = item.article || {};
      const userData = item.user_data || {};

      return {
        id: item.id,
        type: item.type || 'item',
        item_type: item.item_type || 'article',
        collection_id: item.collection_id,
        custom_type: item.custom_type || null,
        deleted: item.deleted || false,
        seq: item.seq,
        drm: item.drm || false,
        ext_ids: extIds,
        primary_file_hash: item.primary_file_hash,
        primary_file_type: item.primary_file_type,
        files: item.files || [],
        import_data: item.import_data || {},
        article: {
          title: article.title || '',
          abstract: article.abstract || '',
          authors: Array.isArray(article.authors) ? article.authors : [],
          journal: article.journal || '',
          journal_abbrev: article.journal_abbrev || '',
          year: article.year || null,
          month: article.month || '',
          issn: article.issn || '',
          isbn: article.isbn || '',
          eissn: article.eissn || '',
          eisbn: article.eisbn || '',
          issue: article.issue || '',
          volume: article.volume || '',
          chapter: article.chapter || '',
          pagination: article.pagination || ''
        },
        custom_metadata: item.custom_metadata || {},
        user_data: {
          citekey: userData.citekey || '',
          color: userData.color || null,
          created: userData.created || null,
          last_read: userData.last_read || null,
          modified: userData.modified || null,
          notes: userData.notes || '',
          rating: userData.rating || null,
          star: userData.star || false,
          tags: userData.tags || [],
          unread: userData.unread || false,
          has_annotations: userData.has_annotations || false,
          view_count: userData.view_count || 0,
          voted_down_count: userData.voted_down_count || 0,
          voted_up_count: userData.voted_up_count || 0
        }
      };
    });
  }

  getItems(params: IDataItemQueryParams, isScroll: boolean = false): Observable<IDisplayItem[]> {
    const endpointUrl = params.list_id ?
      `${this.globalsService.appConfig.environment.baseUrls.sync}/collections/${params.collection_id}/lists/${params.list_id}/items` :
      `${this.globalsService.appConfig.environment.baseUrls.sync}/collections/${params.collection_id}/items`;

    const scrollSession = this.itemsDisplayService.scrollSessions.get(endpointUrl);
    const queryParams = this.getItemsSyncParams(params, isScroll, scrollSession);

    return this.apiService.get(endpointUrl, queryParams)
      .pipe(
        tap((body: IItemDataResponse) => this.itemsDisplayService.scrollSessions.set(endpointUrl, new ScrollSession(body.scroll_id))),

        map((body: IItemDataResponse) => this.mapItemData(body.items || [])),
        map((items: IItem[]) => this.itemsDisplayService.parseItemsForDisplay(items || [])),

        map((items: IDisplayItem[]) => this.checkItemsAlreadyInCitationQueue(items)),

        tap((items: IDisplayItem[]) => this.itemsDisplayService.disableNextScrollSearchIfNeeded(items, isScroll)),
        map((items: IDisplayItem[]) => this.itemsDisplayService.handleScrollingItemPopulation(items, isScroll, scrollSession))
      );
  }

  private checkItemsAlreadyInCitationQueue(items: IDisplayItem[]) {
    items && items.forEach((item: IDisplayItem) => {
      if (!item) return;
      if (this.citationsQueueService.citationQueueItemIds.includes(item.id)) {
        item.isChecked = true;
      }
    });

    return items;
  }

  async getItemsByDocumentCitations(documentCitations: DocumentCitations | CitationSelection, nullsIncluded = false): Promise<DocumentItemsResponse> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.sync}/smartcite/fetch_items`;

    const jobs: (() => Promise<IDisplayItem[]>)[] = this.utilityService.chunkify(documentCitations.ids, BULK_REQUEST_SIZE).map((batch: string[]) => () => {

      const body: DocumentCitations = {
        ids: batch
      };

      return this.apiService.post(url, body).pipe(
        map((items: ISmartciteFetchedItem[]) => this.itemsDisplayService.parseItemsForDisplay(items || [])),
        map((items: IDisplayItem[]) => this.checkItemsAlreadyInCitationQueue(items))
      ).toPromise();
    });

    const items: IDisplayItem[] = await this.utilityService.runInSequenceAsList((jobs));

    const foundItems: IDisplayItem[] = this.utilityService.filterExisting(items);
    const nullItemsCount: number = items.length - foundItems.length;

    return {
      items: nullsIncluded ? items : foundItems,
      nullItemsCount,
      options: (<CitationSelection>documentCitations).options
    };
  }

  async checkOriginalIds(originalIds: string[]): Promise<ItemsByOriginalId> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.sync}/status/check_original_ids`;
    const jobs: (() => Promise<ItemsByOriginalId>)[] = this.utilityService.chunkify(originalIds, BULK_REQUEST_SIZE).map((batch: string[]) => () => {

      const body = {
        original_ids: batch
      };

      return firstValueFrom(this.apiService.post(url, body));
    });

    return await this.utilityService.runInSequenceAsMap((jobs));
  }
}